import { Flex, Spacing, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue'
import { useForm } from '../../../../../../hooks/useForm'
import { useRegisterInvoicingSettingsForm } from '../../../../hooks/useRegisterInvoicingSettingsForm'
import { useRegisterInvoicingSettingsPreviewPatch } from '../../../../hooks/useRegisterInvoicingSettingsPreviewPatch'
import { useZervantSettings } from '../../../../hooks/useZervantSettings'
import type {
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../../../../types/invoicingSettingsFormSave'
import { paymentMethodsFields } from '../../constants/paymentMethodsFields'
import { useGetAutomaticPaymentRemindersSettings } from '../../hooks/useGetAutomaticPaymentRemindersSettings'
import { usePredefinedPaymentMethodsValues } from '../../hooks/usePredefinedPaymentMethodsValues'
import { useUpdateAutomaticPaymentRemindersSettings } from '../../hooks/useUpdateAutomaticPaymentRemindersSettings'
import { getCachedPaymentMethodsPayload } from '../../utils/getCachedPaymentMethodsPayload'
import { getPaymentsAndRemindersPreviewPatch } from '../../utils/getPaymentsAndRemindersPreviewPatch'
import { PaymentsAndRemindersSettingsFormFields } from './elements/PaymentsAndRemindersSettingsFormFields'
import type { PaymentsAndRemindersSettingsFormSchema } from './types/paymentsAndRemindersSettingsFormSchema'
import { getPaymentsAndRemindersSettingsDefaultValues } from './utils/getPaymentsAndRemindersSettingsDefaultValues'
import { getPaymentsAndRemindersSettingsFormSchema } from './utils/getPaymentsAndRemindersSettingsFormSchema'

export const PaymentsAndRemindersSettingsForm = (): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    hasFailedToLoad: hasPaymentMethodsFailedToLoad,
    isLoading: isPaymentMethodsLoading,
    predefinedPaymentMethodsValues,
    refetch: refetchPaymentMethods,
  } = usePredefinedPaymentMethodsValues()
  const {
    automaticPaymentRemindersSettings: paymentRemindersSettings,
    isLoading: isPaymentRemindersLoading,
    refetch: refetchPaymentReminders,
  } = useGetAutomaticPaymentRemindersSettings(t)
  const { mutateAsync: updateReminders } = useUpdateAutomaticPaymentRemindersSettings()
  const { data: zervantSettings } = useZervantSettings()
  const { Form, formState, getValues, handleSubmit, reset, watch } = useForm<PaymentsAndRemindersSettingsFormSchema>({
    defaultValues: getPaymentsAndRemindersSettingsDefaultValues({ t }),
    validationSchema: getPaymentsAndRemindersSettingsFormSchema(t),
  })

  const isLoading = isPaymentMethodsLoading || isPaymentRemindersLoading
  const hasRemindersFailedToLoad = !isPaymentRemindersLoading && !paymentRemindersSettings
  const hasFailedToLoad = !isLoading && (hasPaymentMethodsFailedToLoad || hasRemindersFailedToLoad)
  const hasLoadedSettings = !isLoading && !hasFailedToLoad

  const { isDirty } = formState
  const [bank, iban, offlinePayment, paypalEmail, swift] = watch(paymentMethodsFields)
  // Memoized on the strings: a new object on every render would restart the debounce forever.
  const paymentMethodsValues = useMemo(
    () => ({ bank, iban, offlinePayment, paypalEmail, swift }),
    [bank, iban, offlinePayment, paypalEmail, swift],
  )
  const debouncedPaymentMethodsValues = useDebouncedValue(paymentMethodsValues, LIVE_PREVIEW_DEBOUNCE_TIME)

  const handleFormReset = useCallback(() => {
    reset(getValues())
  }, [getValues, reset])

  const handleRetry = useCallback(
    () => Promise.all([refetchPaymentMethods(), refetchPaymentReminders()]),
    [refetchPaymentMethods, refetchPaymentReminders],
  )

  const handleSave = useCallback(
    ({ updateSettings }: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> => {
      if (!hasLoadedSettings) {
        return Promise.reject(new Error('Payments and reminders settings are not loaded'))
      }

      // handleSubmit passes Zod's parsed values. trigger() plus getValues() would send the raw
      // IBAN, including spaces the schema strips before it checks the number.
      return new Promise((resolve, reject) => {
        handleSubmit(
          async (values) => {
            const { automaticPaymentRemindersSettings, ...paymentMethods } = values

            await Promise.all([
              updateSettings({
                values: {
                  paymentMethods: getCachedPaymentMethodsPayload(paymentMethods, queryClient),
                },
              }),
              updateReminders(automaticPaymentRemindersSettings),
            ])

            // No data invalidation needed: both writers seed their cache from the PUT response.
            resolve('saved')
          },
          () => {
            resolve('invalid')
          },
        )().catch(reject)
      })
    },
    [handleSubmit, hasLoadedSettings, queryClient, updateReminders],
  )

  useRegisterInvoicingSettingsForm({ isDirty, resetForm: handleFormReset, save: handleSave })

  const previewPatch =
    isDirty && zervantSettings
      ? getPaymentsAndRemindersPreviewPatch({
          savedPaymentMethods: zervantSettings.values.paymentMethods,
          savedValues: predefinedPaymentMethodsValues,
          values: debouncedPaymentMethodsValues,
        })
      : undefined

  useRegisterInvoicingSettingsPreviewPatch(previewPatch)

  // Replace the empty defaults with server values once both queries have succeeded.
  // Skip this while the user has unsaved edits, so a refetch cannot wipe them.
  // A failed fetch leaves the defaults in place and the fields unmounted, so Save cannot send them.
  useEffect(() => {
    if (!hasLoadedSettings || isDirty) {
      return
    }

    reset(
      getPaymentsAndRemindersSettingsDefaultValues({
        paymentMethods: predefinedPaymentMethodsValues,
        paymentRemindersSettings,
        t,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: re-running when the form turns clean after save would reset over the values the host just kept
  }, [hasLoadedSettings, predefinedPaymentMethodsValues, paymentRemindersSettings, reset, t])

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <Text variant="h2">{t('invoicing_settings.payments_and_reminders')}</Text>
      <Form>
        <PaymentsAndRemindersSettingsFormFields
          hasFailedToLoad={hasFailedToLoad}
          isLoading={isLoading}
          onRetry={handleRetry}
        />
      </Form>
    </Flex>
  )
}
